import React, {useState, useEffect, useRef} from "react";
import Deployment from "../../components/deployment/deployment";
import {Button} from "../../../../common/styled";
import {EditDeploymentWrapper} from "../../styles/styled";
import jsonScript from './sample_script.json'
import {axiosInstance} from "../../../../config/http";
import {useNavigate} from "react-router-dom";
import {toast} from 'react-toastify';
import {get} from "lodash";
import {isValidJsonString} from "../../../../common/utils";

const DRAFT_KEY = "createDeploymentDraft";

const normalizeDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d) ? '' : d.toISOString().split('T')[0];
};

export default function CreateDeployment() {
  const navigate = useNavigate();

  const sampleDeployment = {
    deployment_name: '',
    instance_name: '',
    user_start_message: '',
    comments: '',
    start_date: '',
    end_date: '',
    script: JSON.stringify(jsonScript, null, 4),
  };

  // init from localStorage draft (if present) else sample
  const [deployment, setDeployment] = useState(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...sampleDeployment,
          ...parsed,
          start_date: normalizeDate(parsed.start_date),
          end_date: normalizeDate(parsed.end_date),
        };
      }
    } catch (_) {}
    return sampleDeployment;
  });

  // normalize on first mount (safety)
  useEffect(() => {
    setDeployment(prev => ({
      ...prev,
      start_date: normalizeDate(prev.start_date),
      end_date: normalizeDate(prev.end_date),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- autosave infra (debounced to localStorage) ---
  const debounceRef = useRef(null);
  const AUTOSAVE_MS = 300;

  const persist = (data) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn("Draft save failed:", e);
    }
  };

  const scheduleAutosave = (data) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(data), AUTOSAVE_MS);
  };

  useEffect(() => {
    scheduleAutosave(deployment);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [deployment]);

  useEffect(() => {
    const handler = () => persist(deployment);
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployment]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDeployment(sampleDeployment);
    toast.info("Draft cleared.");
  };

  const createDeployment = () => {
    if (!isValidJsonString(deployment.script)) {
      toast.error("Syntax error in the script!");
      return;
    }

    axiosInstance.post('/deployment', deployment)
      .then(response => {
        if (get(response, 'data.msg', '') === 'success') {
          toast.success("Survey created!");
          // clear draft after success
          localStorage.removeItem(DRAFT_KEY);
          navigate('/');
        } else if (get(response, 'data.error')) {
          toast.error("Failed! " + get(response, 'data.error'));
        } else {
          toast.error("Failed! Please contact the dev team.");
        }
      })
      .catch(error => {
        console.error(error);
        toast.error("Failed! Please contact the dev team.");
      });
  };

  return (
    <EditDeploymentWrapper>
      {deployment && (
        <Deployment
          deployment={deployment}
          setDeployment={setDeployment}
        />
      )}
      <div style={{display: "flex", gap: 12, marginTop: 12}}>
        <Button onClick={createDeployment}>Create survey</Button>
        <Button onClick={clearDraft} style={{background: "#d9534f"}}>
          Clear draft
        </Button>
      </div>
    </EditDeploymentWrapper>
  );
}
